import { Dictionary } from "lodash";
import {
	ActivityResultsPayload,
	ConsumptionIntervalResultsDTO,
	CountriesEmissionsResultsDTO,
	CountriesResultsPayload,
	DonutChartDTO,
	ElectricityConsumptionResultsDTO,
	EmissionsIntervalResultsDTO,
	EmissionsSitesResultsDTO,
	EnvironmentalKpiDTO,
	FuelConsumptionResultsDTO,
	HeatSteamConsumptionResultsDTO,
	NodesMapDTO,
	NodesMapPayload,
	ProcessGasConsumptionResultsDTO,
	RefrigerantsConsumptionResultsDTO,
	ResultsBasePayload,
	SitesEmissionsBySource,
	WaterConsumptionResultsDTO,
} from "../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { useReadResource, useWriteResource } from "../../../hooks/useResource";

// IMPORTANT TYPE DEFINED FOR PAGE DYNAMIC FILE DOWNLOAD
type FileResource = ReturnType<typeof useWriteResource<ResultsBasePayload, File>>;

export type MapBounds = {
	max_lat: number;
	min_lat: number;
	max_lon: number;
	min_lon: number;
	zoom: number | undefined;
};

interface Options {
	payload: ResultsBasePayload;
	periodType?: "month" | "quarter" | "four-month" | "semester";
	tab: "overview" | "greenrouter" | "thirdparty" | "greenrouterUnits";
	from: string;
	to: string;

	mapBounds?: MapBounds;

	isFiltersLoadingFromStorage?: boolean;
}

export interface OperationalUnitsFileDownload {
	pdfOperationalUnits: FileResource;
	csvOperationalUnits: FileResource;
}

export function useOperationalUnitsData({
	payload,
	periodType,
	tab,
	from,
	to,
	mapBounds,
	isFiltersLoadingFromStorage,
}: Options) {
	const tabQueries: Dictionary<Array<string>> = {
		overview: ["sites", "emissionsIntervals", "sitesSource"],
		greenrouter: ["sites", "intervals", "sitesSource", "consumptionSources", "maps"],
		thirdparty: ["sites", "intervalsThirdParty"],
		greenrouterUnits: ["sites", "datasource"],
	};

	// --- Emissions ---
	const sites = useReadResource<ResultsBasePayload, EmissionsSitesResultsDTO>(
		{ route: ApiRoutes.ResultsSitesEmissions },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("sites"),
		},
		payload
	);

	const intervalsGreenrouter = useReadResource<ResultsBasePayload, EmissionsIntervalResultsDTO>(
		{ route: ApiRoutes.ResultsEmissionsIntervals, query: { activity: "sites" } },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("emissionsIntervals"),
		},
		{ ...payload, period: periodType }
	);

	const sitesSource = useReadResource<ResultsBasePayload, SitesEmissionsBySource>(
		{ route: ApiRoutes.ResultsEmissionsSitesSource },
		{ readMethod: "post", disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("sitesSource") },
		payload
	);

	// --- Consumption ---
	const intervals = useReadResource<ResultsBasePayload, ConsumptionIntervalResultsDTO>(
		{ route: ApiRoutes.ResultsConsumptionIntervals },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("intervals"),
		},
		{ ...payload, period: periodType }
	);

	const electricity = useReadResource<ResultsBasePayload, ElectricityConsumptionResultsDTO>(
		{ route: ApiRoutes.ResultsElectricityConsumption },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("consumptionSources"),
		},
		{ ...payload, period: periodType }
	);

	const water = useReadResource<ResultsBasePayload, WaterConsumptionResultsDTO>(
		{ route: ApiRoutes.ResultsWaterConsumption },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("consumptionSources"),
		},
		{ ...payload, period: periodType }
	);

	const fuel = useReadResource<ResultsBasePayload, FuelConsumptionResultsDTO>(
		{ route: ApiRoutes.ResultsFuelConsumption },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("consumptionSources"),
		},
		{ ...payload, period: periodType }
	);

	const refrigerants = useReadResource<ResultsBasePayload, RefrigerantsConsumptionResultsDTO>(
		{ route: ApiRoutes.ResultsRefrigerantsConsumption },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("consumptionSources"),
		},
		{ ...payload, period: periodType }
	);

	const heatSteam = useReadResource<ResultsBasePayload, HeatSteamConsumptionResultsDTO>(
		{ route: ApiRoutes.ResultsHeatSteamConsumption },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("consumptionSources"),
		},
		{ ...payload, period: periodType }
	);

	const processGas = useReadResource<ResultsBasePayload, ProcessGasConsumptionResultsDTO>(
		{ route: ApiRoutes.ResultsProcessGasConsumption },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("consumptionSources"),
		},
		{ ...payload, period: periodType }
	);

	const nodesMap = useReadResource<NodesMapPayload, NodesMapDTO>(
		{ route: ApiRoutes.ResultGreerouterNodesMap },
		{ readMethod: "post", disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("maps") || !mapBounds },
		{
			...payload,
			from,
			to,
			min_lat: mapBounds?.min_lat || 44.0,
			min_lon: mapBounds?.min_lon || 6.0,
			max_lat: mapBounds?.max_lat || 47.1,
			max_lon: mapBounds?.max_lon || 13.5,
			zoom: mapBounds?.zoom || 6,
		}
	);

	const emiossionsMap = useReadResource<CountriesResultsPayload, CountriesEmissionsResultsDTO>(
		{ route: ApiRoutes.ResultsCountryEmission, query: { source: "nodes" } },
		{ readMethod: "post", disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("maps") },
		{ ...payload, source: "nodes" }
	);

	const intervalsThirdParty = useReadResource<ResultsBasePayload, EmissionsIntervalResultsDTO>(
		{ route: ApiRoutes.ResultsEmissionsIntervals, query: { activity: "sites", dataOrigin: "third-party" } },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("intervalsThirdParty"),
		},
		{ ...payload, period: periodType }
	);

	const emissionsThirdPartyCalculationMethods = useReadResource<ActivityResultsPayload, DonutChartDTO>(
		{ route: ApiRoutes.ResultThirdPartyCalculationMethods },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("intervalsThirdParty"),
		},
		{ ...payload, period: periodType, activity: "sites" }
	);

	const emissionsThirdPartyNodesCertification = useReadResource<ActivityResultsPayload, DonutChartDTO>(
		{ route: ApiRoutes.ResultThirdPartyNodesCertification },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("intervalsThirdParty"),
		},
		{ ...payload, period: periodType, activity: "sites" }
	);

	const emissionsThirdPartyEnvironmentalKpi = useReadResource<ActivityResultsPayload, EnvironmentalKpiDTO>(
		{ route: ApiRoutes.ResultThirdPartyEnvironmentalKpi },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("intervalsThirdParty"),
		},
		{ ...payload, period: periodType, dataOrigin: "third-party", activity: "sites" }
	);

	const sitesSourceThirdParty = useReadResource<ResultsBasePayload, SitesEmissionsBySource>(
		{ route: ApiRoutes.ResultsEmissionsSitesSource },
		{
			readMethod: "post",
			disabled: isFiltersLoadingFromStorage || !tabQueries[tab].includes("intervalsThirdParty"),
		},
		{ ...payload, dataOrigin: "third-party" }
	);

	const isLoadingConsumption =
		electricity.isLoading ||
		water.isLoading ||
		fuel.isLoading ||
		refrigerants.isLoading ||
		heatSteam.isLoading ||
		processGas.isLoading;

	const reloadConsumption = () => {
		electricity.reload();
		water.reload();
		fuel.reload();
		refrigerants.reload();
		heatSteam.reload();
		processGas.reload();
	};

	const isLoadingEmissions =
		sites.isLoading || intervalsGreenrouter.isLoading || intervalsThirdParty.isLoading || sitesSource.isLoading;

	const reloadEmissions =
		sites.reload || intervalsGreenrouter.reload || intervalsThirdParty.reload || sitesSource.reload;

	const pdfOperationalUnits = useWriteResource<ResultsBasePayload, File>(
		{ route: ApiRoutes.OperationalUnitsPDFDownload },
		{ subjectKey: "download" }
	);

	const csvOperationalUnits = useWriteResource<ResultsBasePayload, File>(
		{ route: ApiRoutes.ResultsSitesEmissions, query: { from, to } },
		{
			subjectKey: "download", 
			defaultPayload: { format: "csv-semicolon" }, 
		}
		
	);

	return {
		emissions: {
			sites,
			intervalsGreenrouter,
			intervalsThirdParty,
			sitesSource,
			isLoading: isLoadingEmissions,
			reload: reloadEmissions,
		},
		consumption: {
			intervals,
			sources: {
				electricity,
				water,
				fuel,
				refrigerants,
				heatSteam,
				processGas,
			},
			isLoading: isLoadingConsumption,
			reload: reloadConsumption,
		},
		maps: {
			nodes: {
				nodesData: nodesMap.data,
				isLoading: nodesMap.isLoading,
				reload: nodesMap.reload,
			},
			emissions: {
				emissionData: emiossionsMap.data,
				isLoading: emiossionsMap.isLoading,
				reload: emiossionsMap.reload,
			},
		},
		thirdParty: {
			calucationMethods: emissionsThirdPartyCalculationMethods,
			nodesCertification: emissionsThirdPartyNodesCertification,
			environmanetlKpi: emissionsThirdPartyEnvironmentalKpi,
			siteSource: sitesSourceThirdParty,
		},
		fileDownload: {
			pdfOperationalUnits,
			csvOperationalUnits,
		},
	};
}
